import React, { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { BodyCopy, TooltipContentWrapper } from 'secondstep-components'
import { themeGet } from 'styled-system'

const TOOLTIP_OFFSET = 6

export const TOOLTIP_POSITIONS = {
  leftOffset: rect => ({
    top: rect.bottom + TOOLTIP_OFFSET,
    left: rect.left + TOOLTIP_OFFSET,
  }),
  centered: rect => ({
    top: rect.bottom + TOOLTIP_OFFSET,
    left: rect.left + rect.width / 2,
    transform: 'translateX(-50%)',
  }),
}

const TooltipTrigger = styled.button`
  display: inline-block;
  position: relative;
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${themeGet('colors.blueGray')};
    outline-offset: 2px;
  }

  ${themeGet('breakpoints.tablet')} {
    width: ${themeGet('spacing.purple24_1_5rem')};
  }
`

const PortaledTooltipContent = styled(TooltipContentWrapper)`
  position: fixed;
  width: 15rem;
  padding: 1rem;
  border-radius: 6px;
  color: ${themeGet('colors.darkGray')};
  white-space: normal;
  z-index: 10000;

  ::after {
    content: ' ';
    width: 0.75rem;
    height: 0.75rem;
    position: absolute;
    bottom: 93%;
    left: 50%;
    background-color: ${themeGet('colors.white')};
    transform: translate(-50%) rotate(45deg);
  }

  ${BodyCopy} {
    overflow: visible;
    text-align: left;
    white-space: normal;

    p {
      line-height: 1rem;
      font-size: 0.9125rem;
      overflow: visible;
      white-space: normal;
    }
  }
`

const ReportTableHeaderTooltip = ({
  children,
  className,
  content,
  dataTestId,
  delay = 100,
  positionVariant,
}) => {
  const [active, setActive] = useState(false)
  const [contentStyle, setContentStyle] = useState(null)
  const triggerRef = useRef(null)
  const timeoutRef = useRef(null)
  const getPosition =
    TOOLTIP_POSITIONS[positionVariant] ||
    (rect => ({ top: rect.bottom + TOOLTIP_OFFSET, left: rect.left }))

  const updateContentPosition = () => {
    if (!triggerRef.current) {
      return
    }

    setContentStyle(getPosition(triggerRef.current.getBoundingClientRect()))
  }

  useLayoutEffect(() => {
    if (!active) {
      return undefined
    }

    updateContentPosition()

    const handleReposition = () => updateContentPosition()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [active, positionVariant])

  const showTip = () => {
    timeoutRef.current = setTimeout(() => setActive(true), delay)
  }

  const hideTip = () => {
    clearTimeout(timeoutRef.current)
    setActive(false)
  }

  const contentChildren = content?.props?.children
  let tooltipTitle = ''

  if (typeof contentChildren === 'string') {
    const underscore = '__'
    const firstUnderscore =
      contentChildren.indexOf(underscore) + underscore.length
    const secondUnderscore = contentChildren.indexOf(
      underscore,
      firstUnderscore,
    )
    tooltipTitle = contentChildren.slice(firstUnderscore, secondUnderscore)
  }

  return (
    <>
      <TooltipTrigger
        aria-expanded={active}
        aria-label={`More Information on ${tooltipTitle} Tooltip`}
        className={className}
        data-testid={`${dataTestId}-root-wrapper`}
        onBlur={hideTip}
        onClick={() => (active ? hideTip() : showTip())}
        onPointerEnter={showTip}
        onPointerLeave={hideTip}
        ref={triggerRef}
        role={active ? 'tooltip' : undefined}
        type="button"
      >
        {children}
      </TooltipTrigger>
      {active &&
        contentStyle &&
        createPortal(
          <PortaledTooltipContent
            data-testid={`${dataTestId}-content-wrapper`}
            style={contentStyle}
          >
            {content}
          </PortaledTooltipContent>,
          document.body,
        )}
    </>
  )
}

ReportTableHeaderTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  content: PropTypes.node.isRequired,
  dataTestId: PropTypes.string,
  delay: PropTypes.number,
  positionVariant: PropTypes.oneOf(Object.keys(TOOLTIP_POSITIONS)),
}

export default ReportTableHeaderTooltip
